import { z } from 'zod'
import { ENTITY_TYPES, FRENTE_KEYS } from '../../erp/contract'

/* ⌘K — busca em tudo o que a pessoa pode abrir (server/admin/search-all.ts) e a prévia do que está ligado ao resultado
   em destaque (server/erp/search-related.ts). Sempre POST: o texto buscado (nome, CPF, código) vai no corpo, nunca na
   URL nem no log; o servidor registra só contagens.
   POST /api/v1/admin/search          { q, frente? }      -> SearchAll   resultados em grupos (no máximo 5 por grupo)
   POST /api/v1/admin/search/related  { kind, id, frente? } -> Related  linhas curtas do que está ligado, com link
   `frente`: a frente atual da tela (lembrada no navegador); o servidor confere se o perfil abre essa frente e decide
   por ela quais grupos, telas e linhas a pessoa vê (o menu da frente) e se o nome do padrinho aparece inteiro (pii).
   As respostas passam por zod fechado (.strict()): campo a mais quebra o teste, não vaza. */

export const SEARCH_ALL_MIN = 2
export const SEARCH_ALL_MAX_LENGTH = 80
/** Itens por grupo; o resto fica no "ver mais" (a tela do grupo). */
export const SEARCH_GROUP_MAX = 5

export const SEARCH_GROUPS = ['padrinhos', 'criancas', 'ocorrencias', 'empresas', 'igrejas', 'eventos', 'embaixadores', 'osps', 'equipe', 'mensagens', 'outros'] as const
export type SearchGroupKey = (typeof SEARCH_GROUPS)[number]

export const SEARCH_GROUP_LABELS: Record<SearchGroupKey, string> = {
    padrinhos: 'Padrinhos',
    criancas: 'Crianças',
    ocorrencias: 'Ocorrências',
    empresas: 'Empresas',
    igrejas: 'Igrejas',
    eventos: 'Eventos e consultores',
    embaixadores: 'Embaixadores',
    osps: 'OSPs',
    equipe: 'Equipe',
    mensagens: 'Mensagens',
    outros: 'Pedidos, fichas e campanhas',
}

/** O que a prévia sabe abrir: padrinho e criança pelo GUID do CRM; o resto pela ficha do grafo (tipo + id). */
export const RELATED_KINDS = ['sponsor', 'child', ...ENTITY_TYPES] as const
export type RelatedKind = (typeof RELATED_KINDS)[number]

export const searchAllInputSchema = z.object({
    q: z.string().transform(s => s.trim().replace(/\s+/g, ' ')).pipe(z.string().min(SEARCH_ALL_MIN).max(SEARCH_ALL_MAX_LENGTH)),
    frente: z.enum(FRENTE_KEYS).optional(),
}).strict()
export type SearchAllInput = z.infer<typeof searchAllInputSchema>

export const relatedRefSchema = z.object({ kind: z.enum(RELATED_KINDS), id: z.string().trim().min(1).max(100) }).strict()
export type RelatedRef = z.infer<typeof relatedRefSchema>

export const searchItemSchema = z.object({
    id: z.string(),
    /** Nome como a tela mostra (mascarado quando a frente não vê dado pessoal). */
    label: z.string(),
    sub: z.string().optional(),
    /** Selo curto à esquerda ("Doador", "Evento", "Só no CRM"). */
    badge: z.string().optional(),
    path: z.string(),
    related: relatedRefSchema.nullable(),
}).strict()
export type SearchItem = z.infer<typeof searchItemSchema>

/** ok = respondeu; partial = uma das fontes do grupo não respondeu (as outras sim); unavailable = nenhuma respondeu;
    skipped = a busca não serve para essa fonte (curta demais, CPF sem dado pessoal…). `note` diz o porquê. */
export const SEARCH_GROUP_STATES = ['ok', 'partial', 'unavailable', 'skipped'] as const
export type SearchGroupState = (typeof SEARCH_GROUP_STATES)[number]

export const searchGroupSchema = z.object({
    key: z.enum(SEARCH_GROUPS),
    label: z.string(),
    state: z.enum(SEARCH_GROUP_STATES),
    note: z.string().optional(),
    items: z.array(searchItemSchema).max(SEARCH_GROUP_MAX),
    total: z.number().int().nonnegative(),
    more: z.object({ label: z.string(), path: z.string() }).strict().nullable(),
}).strict()
export type SearchGroup = z.infer<typeof searchGroupSchema>

export const searchAllSchema = z.object({ q: z.string(), groups: z.array(searchGroupSchema) }).strict()
export type SearchAll = z.infer<typeof searchAllSchema>

export const relatedInputSchema = relatedRefSchema.extend({ frente: z.enum(FRENTE_KEYS).optional() }).strict()
export type RelatedInput = z.infer<typeof relatedInputSchema>

export const RELATED_TONES = ['default', 'warn', 'err'] as const
export type RelatedTone = (typeof RELATED_TONES)[number]

export const relatedRowSchema = z.object({
    key: z.string(),
    /** "Crianças", "Em atraso", "Ocorrências"… */
    label: z.string(),
    /** "3 apadrinhadas", "R$ 160 em 2 faturas" */
    value: z.string(),
    detail: z.string().optional(),
    tone: z.enum(RELATED_TONES),
    path: z.string().nullable(),
}).strict()
export type RelatedRow = z.infer<typeof relatedRowSchema>

export const relatedSchema = z.object({
    kind: z.enum(RELATED_KINDS),
    id: z.string(),
    /** Linha de cima: "3 crianças · 1 ocorrência aberta (vencida) · R$ 160 em atraso"; null = nada a dizer. */
    summary: z.string().nullable(),
    rows: z.array(relatedRowSchema),
    /** Fontes que não responderam agora ("Faturas: o CRM ainda está carregando"). */
    notes: z.array(z.string()),
    /** Ficha da pessoa ou entidade no Panel; null = ainda sem ficha (só no CRM). */
    ficha: z.string().nullable(),
}).strict()
export type Related = z.infer<typeof relatedSchema>
